/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { reject, sortBy } from 'lodash'
import { replaceAndStrip } from '../../../../utils/string'
import { CustomAlert, patchCustomAlertRequest, postCustomAlertRequest } from '../../../../states/types/setting'
import Modal from '../../common/Modal'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { Link } from 'react-router-dom'
import { ATTENDANCE_CONDITION_TYPE, CATEGORY_NAME, CONDITION_TYPE, CONDITION_VALUES, OPERATOR_TYPE, OPERATOR_VALUES, TIMING_TYPE, TIMING_VALUES } from '../../../constants/customAlert'
import Tips from '../../common/Tips'
import { compile } from '../../../../utils/search'
import { attendanceTimeToStandard, standardToAttendanceTime } from '../../../../utils/date'

interface CustomAlertModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  customAlertDetail: CustomAlert | undefined,
}

interface Condition {
  condition_type: string,
  operator_type: string,
  detail: string | null,
  target: string | null,
  removable: boolean,
}

interface Timing {
  type: string,
  time: string,
}

interface Errors {
  alert_title: string,
  condition: string,
  team: string,
  category_time: string,
  category_biz_point: string,
  application_time: string,
  application_biz_point: string,
  slack: string,
  timing_type: string,
}

interface PresetList {
  id: number,
  title: string,
  conditions: Condition[],
}

interface CategoryList {
  id: number,
  name: string,
}

interface DisplayTeamMemberList {
  id: number
  name: string
  checked: boolean
  display: boolean
}

interface DisplayTeamList {
  id: number
  name: string
  checked: boolean
  display: boolean
}

interface DisplayApplicationList {
  name: string
  checked: boolean
  display: boolean
}

interface DisplaySlackChannelList {
  id: string
  name: string
  is_private: boolean
  checked: boolean
  display: boolean
}

interface Props {
  customAlertModalState: CustomAlertModalState
  closeCallback: VoidFunction
  createCallback: (params: postCustomAlertRequest) => void
  updateCallback: (params: patchCustomAlertRequest) => void
}

const CustomAlertModal: FC<Props> = (props) => {
  const {
    customAlertModalState,
    closeCallback,
    createCallback,
    updateCallback,
  } = props

  const {
    commonPart,
    teamList,
    system,
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const initCondition = {
    condition_type: "-",
    operator_type: "-",
    detail: "-",
    target: "-",
    removable: false,
  }

  const initTiming = {
    type: "next_day",
    time: "10:00",
  }

  const initErrors = {
    alert_title: "",
    condition: "",
    team: "",
    category_time: "",
    category_biz_point: "",
    application_time: "",
    application_biz_point: "",
    slack: "",
    timing_type: "",
  }

  const [alertTitle, setAlertTitle] = useState<string>("")
  const [conditions, setConditions] = useState<Condition[]>([initCondition])
  const [team, setTeam] = useState<DisplayTeamList[]>([])
  const [timing, setTiming] = useState<Timing>(initTiming)
  const [errors, setErrors] = useState<Errors>(initErrors)
  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [categoryList, setCategoryList] = useState<CategoryList[]>([])
  const [applicationTimeList, setApplicationTimeList] = useState<DisplayApplicationList[]>([])
  const [applicationBizList, setApplicationBizList] = useState<DisplayApplicationList[]>([])
  const [slackChannelList, setSlackChannelList] = useState<DisplaySlackChannelList[]>([])

  const [isSendToMembers, setIsSendToMembers] = useState<boolean>(false)

  const [isDisplayTeamMembersSelectBox, setIsDisplayTeamMembersSelectBox] = useState<boolean>(false)
  const [isDisplayTeamSelectBox, setIsDisplayTeamSelectBox] = useState<boolean>(false)
  const [isDisplayApplicationTimeSelectBox, setIsDisplayApplicationTimeSelectBox] = useState<boolean>(false)
  const [isDisplayApplicationBizSelectBox, setIsDisplayApplicationBizSelectBox] = useState<boolean>(false)
  const [isDisplaySlackChannelListSelectBox, setIsDisplaySlackChannelListSelectBox] = useState<boolean>(false)

  const [searchTeamMembersWord, setSearchTeamMembersWord] = useState<string>("")
  const [searchTeamWord, setSearchTeamWord] = useState<string>("")
  const [searchApplicationTimeWord, setSearchApplicationTimeWord] = useState<string>("")
  const [searchApplicationBizWord, setSearchApplicationBizWord] = useState<string>("")
  const [searchSlackChannelListWord, setSearchSlackChannelListWord] = useState<string>("")

  const [teamMembers, setTeamMembers] = useState<DisplayTeamMemberList[]>([])

  const [selectNoticeType, setSelectNoticeType] = useState<string>("mail")

  const [composition, setComposition] = useState<boolean>(false)

  const [isCustomTiming, setIsCustomTiming] = useState<boolean>(false)

  const timeForSelect = useMemo(() => {
    const times: { label: string, value: string }[] = []
    let h = ""
    let m = ""
    for (let i = 5;i < 29;i += 1) {
      h = (`00${i}`).slice(-2)
      for (let f = 0;f < 2;f += 1) {
        m = (`00${f * 30}`).slice(-2)
        times.push({
          label: `${i ? `${i}時間` : ""}${f ? `${m}分` : ""}${!i && !f ? "0分" : ""}`,
          value: `${h}:${m}`,
        })
      }
    }
    return times
  }, [])

  const modalTitle = useMemo(() => {
    if (customAlertModalState.type === "create") return "カスタムアラートの新規作成"
    if (customAlertModalState.type === "update") return "カスタムアラートの編集"
    if (customAlertModalState.type === "duplicate") return "カスタムアラートの複製"
    return ""
  }, [customAlertModalState.type])

  const selectConditionType = useMemo(() => {
    const select_condition_type = conditions.map((condition) => condition.condition_type)
    return [...CONDITION_TYPE, ...ATTENDANCE_CONDITION_TYPE].filter((v) => select_condition_type.includes(v.value))
  }, [conditions])

  const DisplayConditionType = login.attendance_alert ? [...CONDITION_TYPE, ...ATTENDANCE_CONDITION_TYPE] : CONDITION_TYPE

  const closeModal = () => {
    setIsCustomTiming(false)
    closeCallback()
  }

  const replaceAndStripTitle = (event: React.FocusEvent<HTMLInputElement>) => {
    setAlertTitle(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        alert_title: "",
      })
    }
  }

  const formatDetailNumber = (index: number) => {
    const tmpConditions = [...conditions]
    tmpConditions[index].detail = Number(tmpConditions[index].detail?.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).replace(/[^0-9]/g, '')).toString() || "0"
    setConditions(tmpConditions)
  }

  // conditions
  const addCondition = () => {
    const tmpConditions = conditions.map((condition) => ({ ...condition, removable: true }))
    setConditions([...tmpConditions, {
      ...initCondition,
      removable: true,
    }])
  }

  const deleteCondition = (id: number) => {
    const tmpConditions = conditions.filter((_value, index) => index !== id)
    if (tmpConditions.length === 1) {
      setConditions(tmpConditions.map((condition) => ({ ...condition, removable: false })))
    } else {
      setConditions(tmpConditions)
    }
  }

  const updateConditionType = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
    const tmpConditions = [...conditions]
    tmpConditions[index].condition_type = event.target.value
    // detail
    if (event.target.value === '-') {
      tmpConditions[index].detail = initCondition.detail
      tmpConditions[index].operator_type = initCondition.operator_type
    } else if (
      event.target.value === "biz_point"
      || event.target.value === "biz_point_hour"
      || event.target.value === "category_biz_point"
      || event.target.value === "application_biz_point"
    ) {
      tmpConditions[index].detail = "0"
      tmpConditions[index].operator_type = "less_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    } else if (
      event.target.value === "holiday_work_time"
      || event.target.value === "holiday_active_time"
    ) {
      tmpConditions[index].detail = "0"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    } else if (
      event.target.value === "daily_work_time"
    ) {
      tmpConditions[index].detail = "8"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    } else if (
      event.target.value === "weekly_work_time"
    ) {
      tmpConditions[index].detail = "40"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_week",
      })
    } else if (
      event.target.value === "monthly_work_time"
    ) {
      tmpConditions[index].detail = "180"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_month",
      })
    } else if (
      event.target.value === "daily_over_time"
    ) {
      tmpConditions[index].detail = "2"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    } else if (
      event.target.value === "weekly_over_time"
    ) {
      tmpConditions[index].detail = "10"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_week",
      })
    } else if (
      event.target.value === "monthly_over_time"
    ) {
      tmpConditions[index].detail = "35"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_month",
      })
    } else if (
      event.target.value === "labeling_rate"
    ) {
      tmpConditions[index].detail = "80"
      tmpConditions[index].operator_type = "less_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    } else {
      tmpConditions[index].detail = "00:00"
      tmpConditions[index].operator_type = "more_than_or_equal"
      setTiming({
        ...timing,
        type: "next_day",
      })
    }
    setConditions(tmpConditions)
    if (event.target.value) {
      setErrors({
        ...errors,
        condition: "",
        timing_type: "",
      })
    }
    switch (event.target.value) {
      case "category_time":
        setErrors({
          ...errors,
          category_time: "",
        })
        break
      case "category_biz_point":
        setErrors({
          ...errors,
          category_biz_point: "",
        })
        break
      case "application_time":
        setErrors({
          ...errors,
          application_time: "",
        })
        break
      case "application_biz_point":
        setErrors({
          ...errors,
          application_biz_point: "",
        })
        break
      default:
        break
    }
  }

  const updateOperatorType = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
    const tmpConditions = [...conditions]
    tmpConditions[index].operator_type = event.target.value
    setConditions(tmpConditions)

    if (tmpConditions[index].condition_type === CONDITION_VALUES.END_TIME && tmpConditions[index].operator_type === OPERATOR_VALUES.MORE_THAN_OR_EQUAL) {
      setErrors({
        ...errors,
        timing_type: "",
      })
    }
  }

  const updateDetailSelect = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
    const tmpConditions = [...conditions]
    tmpConditions[index].detail = event.target.value
    setConditions(tmpConditions)
  }

  const updateTargetSelect = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
    const tmpConditions = [...conditions]
    tmpConditions[index].target = event.target.value
    setConditions(tmpConditions)
    if (tmpConditions[index].condition_type === "category_time") {
      setErrors({
        ...errors,
        category_time: "",
      })
    } else if (tmpConditions[index].condition_type === "category_biz_point") {
      setErrors({
        ...errors,
        category_biz_point: "",
      })
    }
  }

  const updateDetailtext = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const tmpValue = event.target.value || "0"
    const tmpConditions = [...conditions]
    tmpConditions[index].detail = tmpValue
    setConditions(tmpConditions)
  }

  const calcDetailtext = (num: number, index: number, limit: number | null) => {
    const tmpConditions = [...conditions]
    if (Number(tmpConditions[index].detail || 0) + num < 0) {
      tmpConditions[index].detail = "0"
    } else if (limit && Number(tmpConditions[index].detail || 0) + num >= limit) {
      tmpConditions[index].detail = limit?.toString()
    } else {
      tmpConditions[index].detail = (Number(tmpConditions[index].detail || 0) + num).toString()
    }
    setConditions(tmpConditions)
  }

  // application time
  const clickApplicationTime = (word: string, index: number) => {
    setIsDisplayApplicationTimeSelectBox(false)
    const list = applicationTimeList?.map((value) => (
      value.name === word
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setApplicationTimeList(list)
    setSearchApplicationTimeWord("")
    const tmpConditions = [...conditions]
    tmpConditions[index].target = word
    setConditions(tmpConditions)
    setErrors({
      ...errors,
      application_time: "",
    })
  }

  const searchApplicationTime = (words: string) => {
    setSearchApplicationTimeWord(words)
    if (!words) {
      setApplicationTimeList(applicationTimeList?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = applicationTimeList?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setApplicationTimeList(sortBy(list, 'id'))
  }

  const resetSearchApplicationTime = () => {
    setSearchApplicationTimeWord("")
    setApplicationTimeList(applicationTimeList?.map((value) => ({ ...value, display: true })))
  }

  const openApplicationTimeSelectBox = () => {
    if (!applicationTimeList.length) return
    setIsDisplayApplicationTimeSelectBox(true)
  }

  // application biz
  const clickApplicationBiz = (word: string, index: number) => {
    setIsDisplayApplicationBizSelectBox(false)
    const list = applicationBizList?.map((value) => (
      value.name === word
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setApplicationBizList(list)
    setSearchApplicationBizWord("")
    const tmpConditions = [...conditions]
    tmpConditions[index].target = word
    setConditions(tmpConditions)
    setErrors({
      ...errors,
      application_biz_point: "",
    })
  }

  const searchApplicationBiz = (words: string) => {
    setSearchApplicationBizWord(words)
    if (!words) {
      setApplicationBizList(applicationBizList?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = applicationBizList?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setApplicationBizList(sortBy(list, 'id'))
  }

  const resetSearchApplicationBiz = () => {
    setSearchApplicationBizWord("")
    setApplicationBizList(applicationBizList?.map((value) => ({ ...value, display: true })))
  }

  const openApplicationBizSelectBox = () => {
    if (!applicationBizList.length) return
    setIsDisplayApplicationBizSelectBox(true)
  }

  const buildControlButton = (num: number, index: number, limit: number | null) => (
    <>
      <button
        type="button"
        onClick={() => calcDetailtext(num, index, limit)}
        style={{
          position: "absolute",
          right: "4px",
          top: "6px",
          padding: 0,
          border: "solid 1px #CCCCCC",
          width: "20px",
          height: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666666",
        }}
      >
        <FontAwesomeIcon icon={['fas', 'angle-up']} />
      </button>
      <button
        type="button"
        onClick={() => calcDetailtext(-num, index, limit)}
        style={{
          position: "absolute",
          right: "4px",
          bottom: "6px",
          padding: 0,
          border: "solid 1px #CCCCCC",
          width: "20px",
          height: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666666",
        }}
      >
        <FontAwesomeIcon icon={['fas', 'angle-down']} />
      </button>
    </>
  )

  const conditionHTML = (
    conditions?.map((condition, index) => (
      <React.Fragment key={`c_a_condition_${condition.operator_type}_${index + 0}`}>
        {
          !!index && (
            <div
              style={{
                position: "relative",
                padding: "4px 8px",
                border: "solid 1px #CCCCCC",
                borderRadius: "10px",
                backgroundColor: "#EEEEEE",
                color: "#999999",
                width: "fit-content",
                margin: "-8px auto",
                zIndex: 1,
              }}
            >
              AND
            </div>
          )
        }
        <div
          className={
            `detail
            equal_space
            ${condition.condition_type === "category_time"
              || condition.condition_type === "category_biz_point"
              || condition.condition_type === "application_time"
              || condition.condition_type === "application_biz_point"
              ? "row_4" : "row_3"}
            ${condition.removable && "removable"}`
          }
          key={`${condition.condition_type}`}
          style={{
            backgroundColor: "#E9EEF0",
            padding: "12px 8px",
          }}
        >
          <div
            style={{
              position: "relative",
              width: condition.condition_type === "category_time"
                || condition.condition_type === "category_biz_point"
                || condition.condition_type === "application_time"
                || condition.condition_type === "application_biz_point"
                ? "31%" : "59%",
            }}
          >
            <select
              value={condition.condition_type}
              onChange={(event) => updateConditionType(event, index)}
              style={{
                width: "100%",
              }}
            >
              <option key="none" value="-">-</option>
              <optgroup label="日次">
                {
                  Array.from(new Set([...DisplayConditionType, ...selectConditionType])).filter((v) => v.type === "daily").map((v) => (
                    <option
                      key={v.value}
                      value={v.value}
                      disabled={
                        selectConditionType.map((w) => w.value).includes(v.value)
                        || (conditions.length > 1 && !!selectConditionType.length && selectConditionType[0].type !== "daily")
                      }
                    >
                      {v.label}
                    </option>
                  ))
                }
              </optgroup>
              <optgroup label="週次">
                {
                  Array.from(new Set([...DisplayConditionType, ...selectConditionType])).filter((v) => v.type === "weekly").map((v) => (
                    <option
                      key={v.value}
                      value={v.value}
                      disabled={
                        selectConditionType.map((w) => w.value).includes(v.value)
                        || (conditions.length > 1 && !!selectConditionType.length && selectConditionType[0].type !== "weekly")
                      }
                    >
                      {v.label}
                    </option>
                  ))
                }
              </optgroup>
              <optgroup label="月次">
                {
                  Array.from(new Set([...DisplayConditionType, ...selectConditionType])).filter((v) => v.type === "monthly").map((v) => (
                    <option
                      key={v.value}
                      value={v.value}
                      disabled={
                        selectConditionType.map((w) => w.value).includes(v.value)
                        || (conditions.length > 1 && !!selectConditionType.length && selectConditionType[0].type !== "monthly")
                      }
                    >
                      {v.label}
                    </option>
                  ))
                }
              </optgroup>
            </select>
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <FontAwesomeIcon icon={['fas', 'caret-down']} />
            </div>
          </div>
          {
            (
              condition.condition_type === "category_time"
              || condition.condition_type === "category_biz_point"
            )
            && (
              <div
                style={{
                  position: "relative",
                  width: "26%",
                }}
              >
                <select
                  value={condition.target ?? "-"}
                  onChange={(event) => updateTargetSelect(event, index)}
                  style={{
                    width: "100%",
                  }}
                >
                  <option key="-" value="-">
                    -
                  </option>
                  {
                    categoryList.map((value: CategoryList) => (
                      <option key={value.id} value={value.id}>
                        {CATEGORY_NAME.find((v) => v.id === value.id)?.label}
                      </option>
                    ))
                  }
                </select>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "8px",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'caret-down']} />
                </div>
              </div>
            )
          }
          {
            (
              condition.condition_type === "application_time"
            )
            && (
              <div className="conditions_item select">
                <div className="selected_item">
                  <div
                    className="modal_trigger"
                    role="none"
                    onClick={openApplicationTimeSelectBox}
                  />
                  <p>
                    {
                      applicationTimeList.find((value) => value.checked)?.name || "-"
                    }
                  </p>
                  <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                </div>
                <div className={`select_box custom_alert_select ${isDisplayApplicationTimeSelectBox || "none"}`}>
                  <div className="search_area">
                    <FontAwesomeIcon
                      icon={['fas', 'search']}
                      className="icon"
                    />
                    <input
                      type="text"
                      placeholder="アプリ名で検索"
                      onChange={(event) => searchApplicationTime(event.target.value)}
                      value={searchApplicationTimeWord}
                    />
                    {
                      searchApplicationTimeWord && (
                        <button type="button" className="reset" onClick={resetSearchApplicationTime}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      )
                    }
                  </div>
                  <div className="select_list">
                    <div
                      key={0}
                      className="select_row single"
                      onClick={() => clickApplicationTime("-", index)}
                      role="none"
                    >
                      -
                    </div>
                    {
                      applicationTimeList && applicationTimeList.map((value: DisplayApplicationList) => (
                        value.display && (
                          <div
                            key={value.name}
                            className="select_row single"
                            onClick={() => clickApplicationTime(value.name, index)}
                            role="none"
                          >
                            {value.name}
                          </div>
                        )
                      ))
                    }
                  </div>
                </div>
              </div>
            )
          }
          {
            (
              condition.condition_type === "application_biz_point"
            )
            && (
              <div className="conditions_item select">
                <div className="selected_item">
                  <div
                    className="modal_trigger"
                    role="none"
                    onClick={openApplicationBizSelectBox}
                  />
                  <p>
                    {
                      applicationBizList.find((value) => value.checked)?.name || "-"
                    }
                  </p>
                  <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                </div>
                <div className={`select_box custom_alert_select ${isDisplayApplicationBizSelectBox || "none"}`}>
                  <div className="search_area">
                    <FontAwesomeIcon
                      icon={['fas', 'search']}
                      className="icon"
                    />
                    <input
                      type="text"
                      placeholder="アプリ名で検索"
                      onChange={(event) => searchApplicationBiz(event.target.value)}
                      value={searchApplicationBizWord}
                    />
                    {
                      searchApplicationBizWord && (
                        <button type="button" className="reset" onClick={resetSearchApplicationBiz}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      )
                    }
                  </div>
                  <div className="select_list">
                    <div
                      key={0}
                      className="select_row single"
                      onClick={() => clickApplicationBiz("-", index)}
                      role="none"
                    >
                      -
                    </div>
                    {
                      applicationBizList && applicationBizList.map((value: DisplayApplicationList) => (
                        value.display && (
                          <div
                            key={value.name}
                            className="select_row single"
                            onClick={() => clickApplicationBiz(value.name, index)}
                            role="none"
                          >
                            {value.name}
                          </div>
                        )
                      ))
                    }
                  </div>
                </div>
              </div>
            )
          }
          {
            condition.detail !== null && condition.condition_type === "-" && (
              <div
                style={{
                  position: "relative",
                  width: "20%",
                }}
              >
                <select
                  value={condition.detail}
                  onChange={(event) => updateDetailSelect(event, index)}
                  style={{
                    width: "100%",
                  }}
                >
                  <option value="-">-</option>
                </select>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "8px",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'caret-down']} />
                </div>
              </div>
            )
          }
          {
            condition.detail !== null
            && (
              condition.condition_type === "biz_point"
              || condition.condition_type === "biz_point_hour"
              || condition.condition_type === "category_biz_point"
              || condition.condition_type === "application_biz_point"
              || condition.condition_type === "daily_work_time"
              || condition.condition_type === "weekly_work_time"
              || condition.condition_type === "monthly_work_time"
              || condition.condition_type === "daily_over_time"
              || condition.condition_type === "weekly_over_time"
              || condition.condition_type === "monthly_over_time"
              || condition.condition_type === "holiday_work_time"
              || condition.condition_type === "holiday_active_time"
              || condition.condition_type === "labeling_rate"
            ) && (
              <div
                style={{
                  width: "20%",
                  position: "relative",
                  display: "flex",
                  alignItems: "baseline",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    width: "calc(100% - 30px)",
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="数値を入力してください"
                    onChange={(event) => updateDetailtext(event, index)}
                    onBlur={() => formatDetailNumber(index)}
                    onKeyDown={(e) => e.key === "Enter" && !composition && formatDetailNumber(index)}
                    onCompositionStart={() => setComposition(true)}
                    onCompositionEnd={() => setComposition(false)}
                    value={condition.detail}
                    style={{
                      width: "100%",
                    }}
                  />
                  {buildControlButton(CONDITION_TYPE.find((v) => v.value === condition.condition_type)?.step || 0, index, CONDITION_TYPE.find((v) => v.value === condition.condition_type)?.limit || null)}
                </div>
                <div
                  style={{
                    width: "26px",
                  }}
                >
                  {CONDITION_TYPE.find((v) => v.value === condition.condition_type)?.unit}
                </div>
              </div>
            )
          }
          {
            condition.detail !== null
            && (
              condition.condition_type === "start_time"
              || condition.condition_type === "end_time"
            ) && (
              <div
                style={{
                  position: "relative",
                  width: "20%",
                }}
              >
                <select
                  value={condition.detail}
                  onChange={(event) => updateDetailSelect(event, index)}
                  style={{
                    width: "100%",
                  }}
                >
                  {
                    timeForSelect.map((v) => (
                      <option key={v.value} value={v.value}>{v.value}</option>
                    ))
                  }
                </select>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "8px",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'caret-down']} />
                </div>
              </div>
            )
          }
          {
            condition.detail !== null
            && condition.condition_type !== "start_time"
            && condition.condition_type !== "end_time"
            && condition.condition_type !== "biz_point"
            && condition.condition_type !== "biz_point_hour"
            && condition.condition_type !== "category_biz_point"
            && condition.condition_type !== "application_biz_point"
            && condition.condition_type !== "daily_work_time"
            && condition.condition_type !== "weekly_work_time"
            && condition.condition_type !== "monthly_work_time"
            && condition.condition_type !== "daily_over_time"
            && condition.condition_type !== "weekly_over_time"
            && condition.condition_type !== "monthly_over_time"
            && condition.condition_type !== "holiday_work_time"
            && condition.condition_type !== "holiday_active_time"
            && condition.condition_type !== "labeling_rate"
            && condition.condition_type !== "-"
            && (
              <div
                style={{
                  position: "relative",
                  width: "20%",
                }}
              >
                <select
                  value={condition.detail}
                  onChange={(event) => updateDetailSelect(event, index)}
                  style={{
                    width: "100%",
                  }}
                >
                  {
                    timeForSelect.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))
                  }
                </select>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "8px",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'caret-down']} />
                </div>
              </div>
            )
          }
          <div
            style={{
              position: "relative",
              width: "11%",
            }}
          >
            <select
              value={condition.operator_type}
              onChange={(event) => updateOperatorType(event, index)}
              style={{
                width: "100%",
              }}
            >
              {
                condition.condition_type === "-" && (
                  <option value="-">-</option>
                )
              }
              {
                (condition.condition_type === "start_time" || condition.condition_type === "end_time") && (
                  OPERATOR_TYPE.map((v) => (
                    <option key={v.value} value={v.value}>{v.label_1}</option>
                  ))
                )
              }
              {
                condition.condition_type !== "start_time" && condition.condition_type !== "end_time" && condition.condition_type !== "-" && (
                  OPERATOR_TYPE.map((v) => (
                    <option key={v.value} value={v.value}>{v.label_2}</option>
                  ))
                )
              }
            </select>
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <FontAwesomeIcon icon={['fas', 'caret-down']} />
            </div>
          </div>
          {
            condition.removable ? (
              <Tips
                description="削除"
                className=""
                icon={false}
              >
                <>
                  <button
                    type="button"
                    onClick={() => deleteCondition(index)}
                    style={{
                      color: "#FF0000",
                      fontSize: "16px",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                  </button>
                </>
              </Tips>
            ) : (
              <div className="spacer" />
            )
          }
        </div>
        {
          condition.condition_type === "category_time" && errors.category_time && (
            <div className="error">
              {errors.category_time}
            </div>
          )
        }
        {
          condition.condition_type === "category_biz_point" && errors.category_biz_point && (
            <div className="error">
              {errors.category_biz_point}
            </div>
          )
        }
        {
          condition.condition_type === "application_time" && errors.application_time && (
            <div className="error">
              {errors.application_time}
            </div>
          )
        }
        {
          condition.condition_type === "application_biz_point" && errors.application_biz_point && (
            <div className="error">
              {errors.application_biz_point}
            </div>
          )
        }
      </React.Fragment>
    ))
  )

  // team
  const clickTeam = (teamId: number) => {
    setIsDisplayTeamSelectBox(false)
    const list = team?.map((value) => (
      value.id === teamId
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setTeam(list)
    const members = teamList.teams.find((value) => value.team_id === teamId)?.members.filter((v) => v.active)
      .map((value) => ({ id: value.id, name: value.name, checked: false, display: true }))
    if (!members) {
      setTeamMembers([])
      return
    }
    setTeamMembers(members)
    setSearchTeamWord("")
  }

  const searchTeam = (words: string) => {
    setSearchTeamWord(words)
    if (!words) {
      setTeam(team?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = team?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setTeam(sortBy(list, 'id'))
  }

  const resetSearchTeam = () => {
    setSearchTeamWord("")
    setTeam(team?.map((value) => ({ ...value, display: true })))
  }

  // team member
  const clickTeamMembers = (memberId: number) => {
    if (!memberId) return
    if (teamMembers?.find((value) => value.id === memberId)?.checked) {
      setTeamMembers(teamMembers?.map((value) => (value.id === memberId ? { ...value, checked: false } : value)))
    } else {
      setTeamMembers(teamMembers?.map((value) => (value.id === memberId ? { ...value, checked: true } : value)))
    }
  }

  const searchTeamMembers = (words: string) => {
    setSearchTeamMembersWord(words)
    if (!words) {
      setTeamMembers(teamMembers?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = teamMembers?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setTeamMembers(sortBy(list, 'id'))
  }

  const resetSearchTeamMembers = () => {
    setSearchTeamMembersWord("")
    setTeamMembers(teamMembers?.map((value) => ({ ...value, display: true })))
  }

  // notice type
  const clickNoticeType = (type: string) => {
    setSelectNoticeType(type)
    setErrors({
      ...errors,
      slack: "",
    })
  }

  // slack channel list
  const clickSlackChannelList = (channelId: string) => {
    if (!slackChannelList) return
    setIsDisplaySlackChannelListSelectBox(false)
    const list = slackChannelList.map((value) => (
      value.id === channelId
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setSlackChannelList(list)
    setSearchSlackChannelListWord("")
  }

  const searchSlackChannelList = (words: string) => {
    setSearchSlackChannelListWord(words)
    if (!words) {
      setSlackChannelList(slackChannelList.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = slackChannelList.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setSlackChannelList(sortBy(list, 'id'))
  }

  const resetSearchSlackChannelList = () => {
    setSearchSlackChannelListWord("")
    setSlackChannelList(slackChannelList.map((value) => ({ ...value, display: true })))
  }

  // timing
  const updateTimingType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const tmpTiming = { ...timing }
    tmpTiming.type = event.target.value
    setTiming(tmpTiming)
    setErrors({
      ...errors,
      timing_type: "",
    })
  }

  const canUseImmediateTiming = (targetConditions: Condition[]): boolean => targetConditions.every((c) => {
    const isStartTime = c.condition_type === CONDITION_VALUES.START_TIME
    const isEndTimeAndMoreThanOperator = c.condition_type === CONDITION_VALUES.END_TIME && c.operator_type === OPERATOR_VALUES.MORE_THAN_OR_EQUAL
    return isStartTime || isEndTimeAndMoreThanOperator
  })

  const updateTimingTime = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const tmpTiming = { ...timing }
    tmpTiming.time = event.target.value
    setTiming(tmpTiming)
  }

  const saveCustomAlert = () => {
    const tmpErrors: Errors = initErrors
    const team_id = team.find((value) => value.checked)?.id
    const slack_channel = slackChannelList.find((value) => value.checked)
    let errorFlg = false
    if (alertTitle === "") {
      tmpErrors.alert_title = "アラート名を入力してください"
      errorFlg = true
    }
    if (selectConditionType.length === 0) {
      tmpErrors.condition = "条件を1つ以上選択してください"
      errorFlg = true
    }
    if (!team_id || team_id === 0) {
      tmpErrors.team = "対象チームを選択してください"
      errorFlg = true
    }
    conditions.forEach((condition) => {
      switch (condition.condition_type) {
        case "category_time":
          if (condition.target === "-") {
            tmpErrors.category_time = "対象分類を選択してください"
            errorFlg = true
          }
          break
        case "category_biz_point":
          if (condition.target === "-") {
            tmpErrors.category_biz_point = "対象分類を選択してください"
            errorFlg = true
          }
          break
        case "application_time":
          if (condition.target === "-") {
            tmpErrors.application_time = "対象アプリケーションを選択してください"
            errorFlg = true
          }
          break
        case "application_biz_point":
          if (condition.target === "-") {
            tmpErrors.application_biz_point = "対象アプリケーションを選択してください"
            errorFlg = true
          }
          break
        default:
          break
      }
    })
    if (selectNoticeType === "slack" && !slack_channel?.id && slackChannelList.length) {
      tmpErrors.slack = "対象のチャンネルを選択してください"
      errorFlg = true
    }

    if (timing.type === TIMING_VALUES.IMMEDIATE && !canUseImmediateTiming(conditions)) {
      tmpErrors.timing_type = "「勤務開始時間:以降/以前」,「勤務終了時間:以降」以外の条件が選択されている時、即時は選択できません"
      errorFlg = true
    }

    setErrors({
      alert_title: tmpErrors.alert_title || "",
      condition: tmpErrors.condition || "",
      team: tmpErrors.team || "",
      category_time: tmpErrors.category_time || "",
      category_biz_point: tmpErrors.category_biz_point || "",
      application_time: tmpErrors.application_time || "",
      application_biz_point: tmpErrors.application_biz_point || "",
      slack: tmpErrors.slack || "",
      timing_type: tmpErrors.timing_type || "",
    })
    if (errorFlg) return

    const customAlertConditions = reject(conditions, { detail: "-" }).map((condition) => {
      if (
        condition.condition_type === "biz_point"
        || condition.condition_type === "biz_point_hour"
        || condition.condition_type === "category_biz_point"
        || condition.condition_type === "application_biz_point"
      ) {
        return {
          condition_type: condition.condition_type,
          operator_type: condition.operator_type,
          biz_point: condition.detail,
          set_hour: null,
          set_minute: null,
          alert_target: condition.target,
          hours: null,
          value: null,
        }
      }
      if (
        condition.condition_type === "daily_work_time"
        || condition.condition_type === "weekly_work_time"
        || condition.condition_type === "monthly_work_time"
        || condition.condition_type === "daily_over_time"
        || condition.condition_type === "weekly_over_time"
        || condition.condition_type === "monthly_over_time"
        || condition.condition_type === "holiday_work_time"
        || condition.condition_type === "holiday_active_time"
      ) {
        return {
          condition_type: condition.condition_type,
          operator_type: condition.operator_type,
          biz_point: null,
          set_hour: null,
          set_minute: null,
          alert_target: condition.target,
          hours: Number(condition.detail),
          value: null,
        }
      }
      if (
        condition.condition_type === "labeling_rate"
      ) {
        return {
          condition_type: condition.condition_type,
          operator_type: condition.operator_type,
          biz_point: null,
          set_hour: null,
          set_minute: null,
          alert_target: condition.target,
          hours: null,
          value: condition.detail,
        }
      }
      const time = condition.detail ? attendanceTimeToStandard(condition.detail).split(':').map(Number) : null
      return {
        condition_type: condition.condition_type,
        operator_type: condition.operator_type,
        biz_point: null,
        set_hour: time?.[0] ?? null,
        set_minute: time?.[1] ?? null,
        alert_target: condition.target,
        hours: null,
        value: null,
      }
    })

    const sendTimingTime = attendanceTimeToStandard(timing.time).split(':').map(Number)
    const teamMemberIds = teamMembers.filter((value) => value.checked).map((value) => value.id)
    let slack_channel_id = null
    let slack_channel_name = null
    let slack_channel_private = false
    if (selectNoticeType === "slack" && slack_channel) {
      slack_channel_id = slack_channel.id
      slack_channel_name = slack_channel.name
      slack_channel_private = slack_channel.is_private
    } else if (selectNoticeType === "slack" && !slack_channel && customAlertModalState.customAlertDetail) {
      slack_channel_id = customAlertModalState.customAlertDetail.slack_channel_id
      slack_channel_name = customAlertModalState.customAlertDetail.slack_channel_name
      slack_channel_private = customAlertModalState.customAlertDetail.slack_channel_private
    }
    if (customAlertModalState.type === "update") {
      updateCallback(
        {
          id: customAlertModalState.id,
          name: alertTitle,
          send_timing_type: timing.type,
          send_timing_hour: sendTimingTime[0],
          send_timing_minute: sendTimingTime[1],
          send_to_member: isSendToMembers,
          team_ids: [Number(team_id)],
          team_member_ids: teamMemberIds,
          custom_alert_conditions: customAlertConditions,
          notice_type: selectNoticeType,
          slack_channel_id: slack_channel_id,
          slack_channel_name: slack_channel_name,
          slack_channel_private: slack_channel_private,
        },
      )
    } else {
      createCallback(
        {
          member_id: commonPart.current_member?.id,
          name: alertTitle,
          send_timing_type: timing.type,
          send_timing_hour: sendTimingTime[0],
          send_timing_minute: sendTimingTime[1],
          send_to_member: isSendToMembers,
          team_ids: [Number(team_id)],
          team_member_ids: teamMemberIds,
          custom_alert_conditions: customAlertConditions,
          notice_type: selectNoticeType,
          slack_channel_id: slack_channel_id,
          slack_channel_name: slack_channel_name,
          slack_channel_private: slack_channel_private,
        },
      )
    }
    closeModal()
  }

  // chips
  const rejectChips = (value: DisplayTeamMemberList | undefined) => {
    if (!value) return
    clickTeamMembers(value.id)
  }

  const buildChips = (list: DisplayTeamMemberList[] | undefined, deletable = false) => {
    if (!list) return ""
    return list.map((value) => (
      <div className="chip" key={value.name}>
        {value.name}
        {
          deletable && (
            <FontAwesomeIcon
              icon={['fas', 'times']}
              className="close_icon"
              onClick={() => rejectChips(value)}
            />
          )
        }
      </div>
    ))
  }

  const openTeamMembersSelectBox = () => {
    if (!teamMembers.length) return
    setIsDisplayTeamMembersSelectBox(true)
  }

  const openTeamSelectBox = () => {
    if (!team.length) return
    setIsDisplayTeamSelectBox(true)
    setErrors({
      ...errors,
      team: "",
    })
  }

  const openSlackChannelListSelectBox = () => {
    if (!slackChannelList.length) return
    setIsDisplaySlackChannelListSelectBox(true)
    setErrors({
      ...errors,
      slack: "",
    })
  }

  const closeSelectBox = () => {
    setIsDisplayTeamMembersSelectBox(false)
    setIsDisplayTeamSelectBox(false)
    setIsDisplayApplicationTimeSelectBox(false)
    setIsDisplayApplicationBizSelectBox(false)
    setIsDisplaySlackChannelListSelectBox(false)
  }

  useEffect(() => {
    if (!customAlertModalState) return
    const { customAlertDetail } = customAlertModalState
    const tmpTeam = (commonPart.role?.includes('administrator'))
      ? teamList.teams
      : teamList.teams?.filter((value) => commonPart.team_manager_ids?.includes(value.team_id))

    if (customAlertModalState.type !== "create" && customAlertDetail?.name) {
      setAlertTitle(customAlertDetail.name)
      setConditions(
        customAlertDetail.custom_alert_conditions.map((condition) => {
          let detail = "0"
          if (
            condition.condition_type === "biz_point"
            || condition.condition_type === "biz_point_hour"
            || condition.condition_type === "category_biz_point"
            || condition.condition_type === "application_biz_point"
          ) {
            detail = condition.biz_point || "0"
          } else if (
            condition.condition_type === "daily_work_time"
            || condition.condition_type === "weekly_work_time"
            || condition.condition_type === "monthly_work_time"
            || condition.condition_type === "daily_over_time"
            || condition.condition_type === "weekly_over_time"
            || condition.condition_type === "monthly_over_time"
            || condition.condition_type === "holiday_work_time"
            || condition.condition_type === "holiday_active_time"
          ) {
            detail = condition.hours?.toString() || "0"
          } else if (
            condition.condition_type === "labeling_rate"
          ) {
            detail = condition.value?.toString() || "0"
          } else {
            detail = standardToAttendanceTime(condition.set_hour ?? 0, condition.set_minute ?? 0)
          }
          return {
            condition_type: condition.condition_type,
            operator_type: condition.operator_type,
            detail: detail,
            target: condition.alert_target,
            removable: customAlertDetail.custom_alert_conditions.length !== 1,
          }
        }),
      )
      if (customAlertModalState.type === "update") {
        const teamMemberList = teamList.teams.find((value) => value.team_id === customAlertDetail.custom_alert_team_ids[0])?.members
          .filter((v) => v.active)
        if (teamMemberList) {
          setTeamMembers(
            teamMemberList?.map((value) => {
              if (customAlertDetail.custom_alert_team_member_ids.includes(value.id)) {
                return { id: value.id, name: value.name, checked: true, display: true }
              }
              return { id: value.id, name: value.name, checked: false, display: true }
            }),
          )
        } else {
          setTeamMembers([])
        }
        if (tmpTeam) {
          setTeam(
            tmpTeam.map((value) => {
              if (customAlertDetail.custom_alert_team_ids.includes(value.team_id)) {
                return { id: value.team_id, name: value.name, checked: true, display: true }
              }
              return { id: value.team_id, name: value.name, checked: false, display: true }
            }),
          )
        } else {
          setTeam([])
        }
        setIsSendToMembers(customAlertDetail.send_to_member)
        if (system.slack_channel_list) {
          const channel_list = system.slack_channel_list?.map((value) => ({ ...value, checked: false, display: true }))
          if (customAlertDetail.slack_channel_id) {
            channel_list.unshift({
              id: customAlertDetail.slack_channel_id,
              name: customAlertDetail.slack_channel_name,
              is_private: customAlertDetail.slack_channel_private,
              checked: true,
              display: true,
            })
          } else {
            channel_list.unshift({ id: "", name: "-", is_private: false, checked: true, display: true })
          }
          setSlackChannelList(channel_list)
        } else {
          setSlackChannelList([])
        }
      } else {
        setTeamMembers([])
        setTeam(
          tmpTeam.map((value) => ({ id: value.team_id, name: value.name, checked: false, display: true })),
        )
        setIsSendToMembers(false)
        if (system.slack_channel_list) {
          setSlackChannelList(
            system.slack_channel_list?.map((value) => ({ ...value, checked: false, display: true })),
          )
        }
      }
      customAlertDetail.custom_alert_conditions.forEach((condition) => {
        if (condition.condition_type === "application_time") {
          setApplicationTimeList(
            applicationTimeList.map((value) => {
              if (condition.alert_target === value.name) {
                return { ...value, checked: true }
              }
              return { ...value }
            }),
          )
        } else if (condition.condition_type === "application_biz_point") {
          setApplicationBizList(
            applicationBizList.map((value) => {
              if (condition.alert_target === value.name) {
                return { ...value, checked: true }
              }
              return { ...value }
            }),
          )
        }
      })
      setTiming({
        type: customAlertDetail.send_timing_type,
        time: standardToAttendanceTime(customAlertDetail.send_timing_hour ?? 0, customAlertDetail.send_timing_minute ?? 0),
      })
      setSelectNoticeType(customAlertDetail.notice_type)
    } else {
      setAlertTitle("")
      setConditions([initCondition])
      setTeam(
        tmpTeam.map((value) => ({ id: value.team_id, name: value.name, checked: false, display: true })),
      )
      setTiming(initTiming)
      setTeamMembers([])
      setIsSendToMembers(false)
      setApplicationTimeList(
        applicationTimeList.map((value) => ({ ...value, checked: false })),
      )
      setApplicationBizList(
        applicationBizList.map((value) => ({ ...value, checked: false })),
      )
      if (system.slack_channel_list) {
        const channel_list = system.slack_channel_list?.map((value) => ({ ...value, checked: false, display: true }))
        channel_list.unshift({ id: "", name: "-", is_private: false, checked: false, display: true })
        setSlackChannelList(channel_list)
      }
      setSelectNoticeType("mail")
    }
    setErrors(initErrors)
  }, [customAlertModalState])

  useEffect(() => {
    setDisplayModal(customAlertModalState.display)
    if (!customAlertModalState.display) {
      closeSelectBox()
    }
  }, [customAlertModalState.display])

  useEffect(() => {
    if (!system.company_service) return
    setCategoryList(system.company_service.category_list)
    setApplicationTimeList(
      system.company_service.application_names.map((value) => ({ name: value, checked: false, display: true })),
    )
    setApplicationBizList(
      system.company_service.application_names.map((value) => ({ name: value, checked: false, display: true })),
    )
  }, [system.company_service])

  return (
    <Modal
      title={modalTitle}
      show={displayModal}
      wrapperClass="custom_alert"
      closeCallback={closeModal}
      closeable
    >
      <>
        <div className="item">
          <div className="title">
            アラート名
          </div>
          <div className="detail row_1">
            <input
              type="text"
              placeholder="アラート名を入力してください"
              onChange={(event) => setAlertTitle(event.target.value)}
              onBlur={(event) => replaceAndStripTitle(event)}
              value={alertTitle}
            />
          </div>
          {
            errors.alert_title && (
              <div className="error">
                {errors.alert_title}
              </div>
            )
          }
        </div>
        <div className="item">
          <div className="title">
            条件（複数選択可）
          </div>
          {conditionHTML}
          {
            errors.condition && (
              <div className="error">
                {errors.condition}
              </div>
            )
          }
          {
            conditions.length < 11 && (
              <div className="add_conditions" role="presentation" onClick={addCondition}>
                <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: '4px' }} fixedWidth />
                条件を追加する
              </div>
            )
          }
        </div>
        <div className="item">
          <div className="title">
            アラート対象
          </div>
          <div className="detail equal_space row_2">
            <div className="conditions_item">
              <div className="selected_item">
                <div
                  className="modal_trigger"
                  role="none"
                  onClick={openTeamSelectBox}
                />
                <p>
                  {
                    team.find((value) => value.checked)?.name || "-"
                  }
                </p>
                <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
              </div>
              <div className={`select_box custom_alert_select ${isDisplayTeamSelectBox || "none"}`}>
                <div className="search_area">
                  <FontAwesomeIcon
                    icon={['fas', 'search']}
                    className="icon"
                  />
                  <input
                    type="text"
                    placeholder="チーム名で検索"
                    onChange={(event) => searchTeam(event.target.value)}
                    value={searchTeamWord}
                  />
                  {
                    searchTeamWord && (
                      <button type="button" className="reset" onClick={resetSearchTeam}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                <div className="select_list">
                  <div
                    key={0}
                    className="select_row single"
                    onClick={() => clickTeam(0)}
                    role="none"
                  >
                    -
                  </div>
                  {
                    team && team.map((value: DisplayTeamList) => (
                      value.display && (
                        <div
                          key={value.id}
                          className="select_row single"
                          onClick={() => clickTeam(value.id)}
                          role="none"
                        >
                          {value.name}
                        </div>
                      )
                    ))
                  }
                </div>
              </div>
            </div>
            <div className="conditions_item">
              <div className="selected_item">
                <div
                  className="modal_trigger"
                  role="none"
                  onClick={openTeamMembersSelectBox}
                />
                {
                  teamMembers.filter((value) => value.checked).length
                    ? ""
                    : team.find((value) => value.checked)?.id && <p>全員</p>
                }
                {
                  buildChips(
                    teamMembers.filter((value) => value.checked),
                    true,
                  )
                }
                <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
              </div>
              <div className={`select_box custom_alert_select ${isDisplayTeamMembersSelectBox || "none"}`}>
                <div className="search_area">
                  <FontAwesomeIcon
                    icon={['fas', 'search']}
                    className="icon"
                  />
                  <input
                    type="text"
                    placeholder="メンバー名で検索"
                    onChange={(event) => searchTeamMembers(event.target.value)}
                    value={searchTeamMembersWord}
                  />
                  {
                    searchTeamMembersWord && (
                      <button type="button" className="reset" onClick={resetSearchTeamMembers}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                <div className="select_list">
                  {
                    teamMembers && teamMembers.map((value: DisplayTeamMemberList) => (
                      value.display && (
                        <div
                          key={value.id}
                          className="select_row"
                        >
                          <input
                            type="checkbox"
                            id={`filter_team_member_${value.name}_${value.id}`}
                            name="team_members"
                            value={value.id}
                            checked={value.checked}
                            onChange={() => clickTeamMembers(value.id)}
                          />
                          <label htmlFor={`filter_team_member_${value.name}_${value.id}`}>
                            {value.name}
                          </label>
                        </div>
                      )
                    ))
                  }
                </div>
              </div>
            </div>
          </div>
          {
            errors.team && (
              <div className="error">
                {errors.team}
              </div>
            )
          }
        </div>
        <div className="item">
          <div className="title">
            通知方法
          </div>
          <div
            className="detail row_1"
            style={{
              display: "flex",
              gap: "8px",
            }}
          >
            <label htmlFor="mail">
              <input
                type="radio"
                id="mail"
                name="notice"
                value="mail"
                checked={selectNoticeType === "mail"}
                onClick={(e) => clickNoticeType(e.currentTarget.value)}
              />
              メール
            </label>
            {
              (system.mail_delivery?.slack_integration || customAlertModalState.customAlertDetail?.notice_type === "slack") ? (
                <label htmlFor="slack">
                  <input
                    type="radio"
                    id="slack"
                    name="notice"
                    value="slack"
                    checked={selectNoticeType === "slack"}
                    onClick={(e) => clickNoticeType(e.currentTarget.value)}
                  />
                  Slack
                </label>
              ) : (
                <Link
                  to={setPath(URL.SETTING_SLACK.path, "ja")}
                >
                  slack連携すると指定のチャンネルに通知できます
                </Link>
              )
            }
          </div>
        </div>
        <div className="item">
          <div className="title">
            送信対象
          </div>
          <div className="detail row_1">
            {
              selectNoticeType === "mail" && (
                <>
                  <label htmlFor="send_to_manager">
                    <input
                      type="checkbox"
                      id="send_to_manager"
                      defaultChecked
                      disabled
                    />
                    マネージャー
                  </label>
                  <label htmlFor="send_to_member">
                    <input
                      type="checkbox"
                      id="send_to_member"
                      onChange={(event) => setIsSendToMembers(event.target.checked)}
                      checked={isSendToMembers}
                    />
                    対象のメンバー
                  </label>
                </>
              )
            }
            {
              system.mail_delivery?.slack_integration && selectNoticeType === "slack" && (
                <div className="slack_channel_item">
                  <div className="selected_item">
                    <div
                      className="modal_trigger"
                      role="none"
                      onClick={openSlackChannelListSelectBox}
                    />
                    <p>
                      {
                        slackChannelList.find((value) => value.checked)?.id && slackChannelList.find((value) => value.checked)?.is_private && (
                          <FontAwesomeIcon icon={['fas', 'lock']} className="pre_icon" />
                        )
                      }
                      {
                        slackChannelList.find((value) => value.checked)?.id && !slackChannelList.find((value) => value.checked)?.is_private && (
                          <FontAwesomeIcon icon={['fas', 'hashtag']} className="pre_icon" />
                        )
                      }
                      {
                        slackChannelList.find((value) => value.checked)?.name || "-"
                      }
                    </p>
                    <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                  </div>
                  <div className={`select_box slack_channel_select ${isDisplaySlackChannelListSelectBox || "none"}`}>
                    <div className="search_area">
                      <FontAwesomeIcon
                        icon={['fas', 'search']}
                        className="icon"
                      />
                      <input
                        type="text"
                        placeholder="チャンネル名で検索"
                        onChange={(event) => searchSlackChannelList(event.target.value)}
                        value={searchSlackChannelListWord}
                      />
                      {
                        searchSlackChannelListWord && (
                          <button type="button" className="reset" onClick={resetSearchSlackChannelList}>
                            <FontAwesomeIcon icon={['far', 'times-circle']} />
                          </button>
                        )
                      }
                    </div>
                    <div className="select_list">
                      {
                        slackChannelList && slackChannelList.map((value: DisplaySlackChannelList, index: number) => (
                          value.display && (
                            <div
                              key={value.id}
                              className="select_row single"
                              onClick={() => clickSlackChannelList(value.id)}
                              role="none"
                            >
                              {
                                value.id && value.is_private && (
                                  <FontAwesomeIcon icon={['fas', 'lock']} className="pre_icon" />
                                )
                              }
                              {
                                value.id && !value.is_private && (
                                  <FontAwesomeIcon icon={['fas', 'hashtag']} className="pre_icon" />
                                )
                              }
                              {value.name}
                              {
                                index === 0 && customAlertModalState.type === "update" && (
                                  <>
                                    （現在の設定）
                                  </>
                                )
                              }
                            </div>
                          )
                        ))
                      }
                    </div>
                  </div>
                </div>
              )
            }
            {
              !system.mail_delivery?.slack_integration && selectNoticeType === "slack" && (
                <div className="detail">
                  {
                    customAlertModalState.customAlertDetail?.slack_channel_private && (
                      <FontAwesomeIcon icon={['fas', 'lock']} className="pre_icon" />
                    )
                  }
                  {
                    !customAlertModalState.customAlertDetail?.slack_channel_private && (
                      <FontAwesomeIcon icon={['fas', 'hashtag']} className="pre_icon" />
                    )
                  }
                  {
                    customAlertModalState.customAlertDetail?.slack_channel_name
                  }
                </div>
              )
            }
          </div>
          {
            errors.slack && (
              <div className="error">
                {errors.slack}
              </div>
            )
          }
        </div>
        <div className="item">
          <div className="title">
            送信タイミング
          </div>
          <div
            className="detail equal_space row_2"
            style={{
              gap: "4px",
            }}
          >
            {
              isCustomTiming ? (
                <>
                  <div
                    style={{
                      position: "relative",
                      width: "50%",
                    }}
                  >
                    <select
                      value={timing.type}
                      onChange={(event) => updateTimingType(event)}
                      style={{
                        width: "100%",
                      }}
                    >
                      {
                        TIMING_TYPE.map((v) => (
                          <option
                            key={v.value}
                            value={v.value}
                            disabled={v.value === TIMING_VALUES.IMMEDIATE && !canUseImmediateTiming(conditions)}
                          >
                            {v.label}
                          </option>
                        ))
                      }
                    </select>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "8px",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'caret-down']} />
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      width: "50%",
                    }}
                  >
                    {(() => {
                      const isDisabled = timing.type === TIMING_VALUES.IMMEDIATE
                      const baseStyle = { width: "100%" }
                      const disabledStyle = {
                        ...baseStyle,
                        backgroundColor: "#F5F5F5",
                        borderColor: "#DDDDDD",
                        cursor: "not-allowed",
                        color: "#999999",
                      }
                      return (
                        <select
                          value={timing.time}
                          onChange={(event) => updateTimingTime(event)}
                          disabled={isDisabled}
                          style={isDisabled ? disabledStyle : baseStyle}
                        >
                          {
                            timeForSelect.map((v) => (
                              <option key={v.value} value={v.value}>{v.value}</option>
                            ))
                          }
                        </select>
                      )
                    })()}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "8px",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'caret-down']} />
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    {TIMING_TYPE.find((v) => v.value === timing.type)?.label}
                    {timing.type === TIMING_VALUES.IMMEDIATE ? "" : timing.time}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCustomTiming(true)}
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      fontSize: "10px",
                      padding: "4px 8px",
                    }}
                  >
                    送信タイミングを細かく設定する
                  </button>
                </div>
              )
            }
          </div>
          {
            errors.timing_type && (
              <div className="error">
                {errors.timing_type}
              </div>
            )
          }
        </div>
        <div
          className={`
            close_screen
            ${
              (isDisplayTeamMembersSelectBox
                || isDisplayTeamSelectBox
                || isDisplayApplicationTimeSelectBox
                || isDisplayApplicationBizSelectBox
                || isDisplaySlackChannelListSelectBox
              ) || 'none'}
          `}
          role="presentation"
          onClick={closeSelectBox}
        />
      </>
      <>
        <button type="button" className="button cancel" onClick={closeCallback}>
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={saveCustomAlert}
        >
          アラートを保存する
        </button>
      </>
    </Modal>
  )
}

export default CustomAlertModal
